import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormControl, Link, Typography, Box } from '@material-ui/core';
import { ModalActionsComponent, PasslySelect, PasslyButton, PasslyCheckbox, PasslyTextField } from '@app/components';
import { getLoaderBtn, setUserApns, setUserPatchData } from '@app/store/actions';
import { IUserDetail } from '@app/models';
import { usersService } from '@app/services';
import { useTypedTranslation } from '@app/hooks';
import { xorBy } from 'lodash';
import { AxiosResponse } from 'axios';
import { emptyGuid, provisioningStates, onPressEnterModal, genericErrorString, userDisplayNameRegex } from '@app/helpers';
const validator = require('validator');

export interface IEditUserData {
    person: IUserDetail;
    editUserCompleteCallback: Function;
    isDirSync: boolean;
}

const initialState = {
    displayName: '',
    displayNameInvalid: false,
    emailAddress: '',
    emailAddressInvalid: false,
    apns: [] as any[],
    newApn: '',
    apnTaken: false,
    apnInvalid: false,
    provisioningSelected: '',
    provisionRequire2FA: true,
    provisionSendEmail: false,
    patchUserErrorMessage: '',
    apnsDone: false,
    patchDone: false,
    shouldReloadUser: false
};

export default function EditUserDialog(props: IEditUserData) {
    // -------------------------------------------
    // ----------------- DATA --------------------
    // -------------------------------------------
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const { editUserCompleteCallback, isDirSync: dirSync, person } = props;

    const [
        {
            displayName,
            displayNameInvalid,
            emailAddress,
            emailAddressInvalid,
            apns,
            newApn,
            apnTaken,
            apnInvalid,
            provisioningSelected,
            provisionRequire2FA,
            provisionSendEmail,
            patchUserErrorMessage,
            apnsDone,
            patchDone,
            shouldReloadUser
        },
        setFormState
    ] = useState(initialState);

    // -------------------------------------------
    // ----------------- APIs --------------------
    // -------------------------------------------
    const patchUserAPI = (patchObject: any) => {
        usersService
            .patchUser(person.Id, patchObject)
            .then(() => {
                if (provisioningSelected === 'Provisioned' && provisionSendEmail) {
                    usersService.onboardMany({ People: [person.Id], MFA: provisionRequire2FA });
                }

                setFormState((previousState) => ({ ...previousState, patchDone: true }));
                dispatch(setUserPatchData(patchObject));
            })
            .catch((error: any) => {
                dispatch(getLoaderBtn(false));
                setFormState((previousState) => ({ ...previousState, patchUserErrorMessage: genericErrorString(error) }));
            });
    };

    const setUserAPNsAPI = (apnsToAdd: any[], hasAdded: boolean) => {
        usersService
            .setAlternatePrincipalNames(person.Id, { apns: apnsToAdd.map((apn) => apn.PrincipalName) })
            .then(() => {
                if (!hasAdded) {
                    dispatch(setUserApns(apnsToAdd));
                } else {
                    setFormState((previousState) => ({ ...previousState, shouldReloadUser: true }));
                }

                setFormState((previousState) => ({ ...previousState, apnsDone: true }));
            })
            .catch();
    };

    const isPrincipalNameAvailableAPI = () => {
        usersService.isPrincipalNameAvailable(person.OrganizationId, newApn, null).then((response: AxiosResponse) => {
            if (response.data.value) {
                setFormState((previousState) => ({ ...previousState, apns: apns.concat({ PrincipalName: newApn, IsSynced: false }) }));
                setFormState((previousState) => ({ ...previousState, apnTaken: false }));
                setFormState((previousState) => ({ ...previousState, newApn: '' }));
            } else {
                setFormState((previousState) => ({ ...previousState, apnTaken: true }));
            }

            setFormState((previousState) => ({ ...previousState, apnsDone: false }));
        });
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useEffect(() => {
        setFormState((previousState) => ({ ...previousState, displayName: person.DisplayName }));
        setFormState((previousState) => ({ ...previousState, emailAddress: person.EmailAddress }));

        let userApns: any[] = person.AlternatePrincipalNames.map(({ Id, PrincipalName, IsSynced }) => ({ Id, PrincipalName, IsSynced }));
        setFormState((previousState) => ({
            ...previousState,
            apns: userApns
        }));
        setFormState((previousState) => ({ ...previousState, provisioningSelected: person.ProvisioningState }));
    }, [person]);

    useEffect(() => {
        // Processing complete on page - cleanup and close
        if (apnsDone && patchDone) {
            handleClose();
        }
    }, [apnsDone, patchDone]);

    // -------------------------------------------
    // ----------------- FUNCTIONS ---------------
    // -------------------------------------------
    const onFormChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const { name } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: checked }));
    };
    const onDisplayNameChange = (e: any) => {
        let displayName = e.target.value;
        setFormState((previousState) => ({ ...previousState, displayName: displayName }));

        let regExp = new RegExp(userDisplayNameRegex);

        if (regExp.test(displayName)) {
            setFormState((previousState) => ({ ...previousState, displayNameInvalid: false }));
        } else {
            setFormState((previousState) => ({ ...previousState, displayNameInvalid: true }));
        }
    };

    const onEmailAddressChange = (e: any) => {
        let email = e.target.value;

        setFormState((previousState) => ({ ...previousState, emailAddress: email }));

        if (validator.isEmail(email)) {
            setFormState((previousState) => ({ ...previousState, emailAddressInvalid: false }));
        } else {
            setFormState((previousState) => ({ ...previousState, emailAddressInvalid: true }));
        }
    };

    const patchUser = async () => {
        let userPatch: any = {};
        let patchChanged: boolean = false;

        if (person.SyncSource === emptyGuid) {
            if (displayName !== person.DisplayName) {
                userPatch.DisplayName = displayName.trim();
                patchChanged = true;
            }
            if (emailAddress !== person.EmailAddress) {
                userPatch.EmailAddress = emailAddress.trim();
                patchChanged = true;
            }
        }

        if (provisioningSelected !== person.ProvisioningState) {
            // provisioning state can change for synced users
            userPatch.ProvisioningState = provisioningSelected;
            patchChanged = true;
        }

        if (patchChanged) {
            patchUserAPI(userPatch);
        } else {
            setFormState((previousState) => ({ ...previousState, patchDone: true }));
        }
    };

    const setApns = () => {
        let apnsWithId: any[] = apns.map(({ Id, PrincipalName }) => ({ Id, PrincipalName }));
        let myDifference: any[] = xorBy(
            apnsWithId,
            person.AlternatePrincipalNames.map(({ Id, PrincipalName }) => ({ Id, PrincipalName })),
            'PrincipalName'
        );

        if (myDifference.length > 0) {
            let hasAdded: boolean = myDifference.filter((apns) => apns.Id === undefined).length > 0;

            setUserAPNsAPI(apnsWithId, hasAdded);
        } else {
            setFormState((previousState) => ({ ...previousState, apnsDone: true }));
        }
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));

        setApns();
        patchUser();
    };

    const handleClose = () => {
        dispatch(getLoaderBtn(false));
        let shouldReload: boolean = shouldReloadUser;
        setFormState(initialState);
        editUserCompleteCallback(shouldReload);
    };

    const tryAddAPN = () => {
        // test for a valid format
        if (!/^[.\w_'-]*@*[.\w_-]*$/.test(newApn)) {
            setFormState((previousState) => ({ ...previousState, apnInvalid: true }));
            return;
        } else {
            setFormState((previousState) => ({ ...previousState, apnInvalid: false }));
        }

        let principalNamesOnly: string[] = apns.map((apn) => apn.PrincipalName);

        if (principalNamesOnly.indexOf(newApn) === -1) {
            isPrincipalNameAvailableAPI();
        } else {
            setFormState((previousState) => ({ ...previousState, newApn: '' }));
        }
    };

    const removeApn = (deleteApn: string) => {
        let removedApnsList = apns.filter(function (apn) {
            return apn.PrincipalName.toLowerCase() !== deleteApn.toLowerCase();
        });

        setFormState((previousState) => ({ ...previousState, apns: removedApnsList }));
    };

    return (
        <>
            <form
                className='modal__form'
                method='post'
                tabIndex={0}
                onKeyPress={onPressEnterModal(handleSubmitBtn, displayNameInvalid || emailAddressInvalid)}
            >
                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'DISPLAY_NAME'}
                        autoComplete={translation('DISPLAY_NAME')}
                        name='displayName'
                        value={displayName}
                        onChange={onDisplayNameChange}
                        id='displayName-input'
                        autoFocus
                        disabled={dirSync}
                        required
                        error={displayNameInvalid}
                        helperText={displayNameInvalid && translation('INVALID_DISPLAY_NAME')}
                    />
                </FormControl>

                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'EMAIL_ADDR'}
                        autoComplete={translation('EMAIL_ADDR')}
                        name='emailAddress'
                        value={emailAddress}
                        onChange={onEmailAddressChange}
                        id='emailAddress-input'
                        disabled={dirSync}
                        required
                        error={emailAddressInvalid}
                        helperText={emailAddressInvalid && translation('INVALID_EMAIL')}
                    />
                </FormControl>

                <FormControl>
                    <PasslyTextField
                        fullWidth
                        label={'USERNAME'}
                        autoComplete={translation('USERNAME')}
                        value={person.PrincipalName}
                        disabled={true}
                        id='username-input'
                    />
                </FormControl>
                <FormControl>
                    <PasslySelect
                        name='provisioningSelected'
                        options={provisioningStates}
                        value={provisioningSelected}
                        onChange={onFormChange}
                        id='select-input'
                        label={'PROVISIONING_POLICY'}
                        appendTo={document.body}
                    />
                </FormControl>
                {provisioningSelected === 'Provisioned' && (
                    <FormControl>
                        <PasslyCheckbox
                            name='provisionSendEmail'
                            checked={provisionSendEmail}
                            onChange={onCheckboxChange}
                            label={translation('NOTIFY_USER')}
                        />
                        <PasslyCheckbox
                            name='provisionRequire2FA'
                            checked={provisionRequire2FA}
                            onChange={onCheckboxChange}
                            label={translation('ENFORCE_MFA')}
                        />
                    </FormControl>
                )}

                <div className='modal__body--row'>
                    <h5>{translation('APN_TITLE')}</h5>
                    {apns.length > 0 ? (
                        apns.map((item: any, index: number) =>
                            item.IsSynced ? (
                                <Typography key={index} variant='body1' color='textSecondary'>
                                    {item.PrincipalName}
                                </Typography>
                            ) : (
                                <div key={index} className='modal__body--spaced_content'>
                                    <Typography variant='body1'>{item.PrincipalName}</Typography>
                                    <Link onClick={() => removeApn(item.PrincipalName)}>&times;</Link>
                                </div>
                            )
                        )
                    ) : (
                        <div className='modal__body--subtitle'>{translation('NO_APNS_SET')}</div>
                    )}
                </div>

                <FormControl className='modal__body--row'>
                    <PasslyTextField
                        fullWidth
                        label={'APN_LABEL'}
                        autoComplete={translation('APN_LABEL')}
                        name='newApn'
                        value={newApn}
                        onChange={onFormChange}
                        id='username-input'
                        error={apnTaken || apnInvalid}
                        helperText={
                            (apnTaken && translation('INVALID_TAKEN_PRINCIPAL_NAME')) || (apnInvalid && translation('INVALID_PRINCIPAL_NAME'))
                        }
                        customButton={
                            <PasslyButton
                                className='modal__body__field--button'
                                variant='contained'
                                color='primary'
                                onClick={tryAddAPN}
                                disabled={newApn === ''}
                            >
                                {translation('ADD_TAB')}
                            </PasslyButton>
                        }
                    />
                </FormControl>

                {patchUserErrorMessage !== '' && (
                    <p>
                        <div className='modal__error'>{patchUserErrorMessage}</div>
                    </p>
                )}
            </form>
            <ModalActionsComponent
                primaryBtn={translation('OK')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleClose}
                disabled={displayNameInvalid || emailAddressInvalid}
                id='editUser'
            />
        </>
    );
}
